// Aba "🔎 Conciliação de trocas" da tela Dispositivos: cada troca realizada (provas do IW, da SOS e
// do Quality) com o aditivo do material no IW e a regra do contrato. Montada no servidor
// (api/_conciliacao-dispositivos.js).
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { supabase } from '../supabase';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#4f46e5' };
const fmtData = iso => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
const CONF = { certo: ['#dcfce7', '#166534', 'IW ok'], provavel: ['#fef9c3', '#854d0e', 'provável'], verificar: ['#fee2e2', '#991b1b', 'verificar'], 'nao encontrado': ['#f1f5f9', '#475569', 'não achado no IW'] };
const PEND_COR = { 'falta aditivo do material': ['#fee2e2', '#991b1b'], 'sem aditivo (custo no pacote)': ['#ffedd5', '#9a3412'], 'sem aditivo (conferir regra)': ['#fef3c7', '#92400e'], 'verificar paciente': ['#fef3c7', '#92400e'] };

export default function ConciliacaoTrocas() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [fam, setFam] = useState('');
  const [pend, setPend] = useState('');
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setCarregando(true); setErro('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/conciliacao-dispositivos', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
      setDados(j);
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  const linhas = dados?.linhas || [];
  const daFam = useMemo(() => linhas.filter(l => !fam || l.familiaNome === fam), [linhas, fam]);
  const pendencias = useMemo(() => {
    const c = {};
    daFam.forEach(l => l.pendencias.forEach(p => { c[p] = (c[p] || 0) + 1; }));
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [daFam]);
  const q = busca.trim().toLowerCase();
  const vis = useMemo(() => daFam.filter(l => (!pend || (pend === 'ok' ? !l.pendencias.length : l.pendencias.includes(pend)))
    && (!q || `${l.paciente} ${l.identificacao.nomeIW}`.toLowerCase().includes(q))), [daFam, pend, q]);

  if (carregando) return <div style={{ color: P.muted }}>Juntando as trocas do IW, da SOS e do Quality…</div>;
  if (erro) return <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px' }}>Não foi possível carregar: {erro}</div>;
  const r = dados.resumo;
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: P.muted, background: P.bg, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9', fontSize: '0.8rem' };
  const cartao = (rotulo, valor, sub, cor) => (
    <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 14px', minWidth: 160 }}>
      <div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 600 }}>{rotulo}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: cor }}>{valor}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: P.muted }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: '0.8rem', color: P.muted }}>
        Trocas <strong>realizadas</strong> desde {fmtData(r.desde)}, juntando as provas: evolução de troca no IW, remoção cobrada pela SOS para troca, troca hospitalar "Realizado" e controle de cânulas em casa no Quality.
        Para cada uma: o <strong>aditivo do material</strong> (cânula, sonda, botton…) no IW e a regra do contrato (material incluso ou excluso).
        <button onClick={carregar} style={{ marginLeft: 8, border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '3px 8px', cursor: 'pointer', display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '0.74rem' }}><RefreshCw size={12} /> Atualizar</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {cartao('Trocas realizadas', r.trocas, `${r.hospitalares} no hospital · ${r.domiciliares} em casa`, P.text)}
        {cartao('Com aditivo do material', r.comAditivo, '', '#166534')}
        {cartao('🔴 Falta aditivo (material excluso)', r.faltaAditivo, 'cobrar da operadora', '#991b1b')}
        {cartao('🟠 Sem aditivo (custo no pacote)', r.semAditivoPacote, 'registrar o custo', '#9a3412')}
        {cartao('Sem aditivo — conferir regra', r.semAditivoConferir, 'contrato não fala do material', '#92400e')}
        {cartao('Sem evolução de troca no IW', r.semEvolucaoIW, 'troca feita, não registrada no prontuário', '#1e40af')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[['', `Todos · ${linhas.length}`], ...r.porFamilia.map(([f, n]) => [f, `${f} · ${n}`])].map(([k, l]) => (
          <button key={k || 'todos'} onClick={() => { setFam(k); setPend(''); }}
            style={{ border: `1px solid ${fam === k ? P.primary : P.border}`, background: fam === k ? '#eef2ff' : P.white, color: fam === k ? P.primary : P.muted, borderRadius: 999, padding: '6px 14px', fontSize: '0.8rem', fontWeight: fam === k ? 800 : 600, cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: '0.76rem', color: P.muted }}>Pendência:</span>
        {[['', 'Todas'], ...pendencias.map(([p, n]) => [p, `${p} · ${n}`]), ['ok', `Sem pendência · ${daFam.filter(l => !l.pendencias.length).length}`]].map(([k, l]) => {
          const ativo = pend === k;
          const [fundo, cor] = PEND_COR[k] || ['#f1f5f9', '#334155'];
          return <button key={k || 'todas'} onClick={() => setPend(k)} style={{ ...chip(ativo ? fundo : P.white, cor), border: `1px solid ${ativo ? cor : P.border}`, cursor: 'pointer', padding: '4px 10px' }}>{l}</button>;
        })}
        <label style={{ position: 'relative', marginLeft: 'auto' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente</span>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Paciente" style={{ padding: '6px 8px 6px 28px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem', width: 190 }} />
        </label>
      </div>
      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
            <thead><tr>{['Data da troca', 'Paciente', 'Dispositivo', 'Onde', 'Provas da troca', 'Aditivo do material', 'Pendências'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {vis.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: P.muted, padding: 26 }}>Nada neste filtro.</td></tr>}
              {vis.map(l => {
                const [cf, cc, ct] = CONF[l.identificacao.confianca] || CONF['nao encontrado'];
                return (
                  <tr key={l.id} style={{ background: l.pendencias.includes('falta aditivo do material') ? '#fff7f7' : undefined }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(l.data)}</td>
                    <td style={{ ...td, fontWeight: 700, maxWidth: 230 }}>
                      {l.identificacao.nomeIW || l.paciente}
                      <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <span style={chip(cf, cc)} title={l.identificacao.motivo || ''}>{ct}</span>
                        {l.identificacao.numIW && <span style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>IW {l.identificacao.numIW}{l.identificacao.atendimentos > 1 ? ` · ${l.identificacao.atendimentos} atendimentos` : ''}</span>}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>{[l.convenio, l.identificacao.unidade].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td style={td}>{l.familiaNome}</td>
                    <td style={td}>{l.local === 'Hospitalar' ? '🏥 Hospital' : l.local === 'Domiciliar' ? '🏠 Casa' : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                    <td style={{ ...td, maxWidth: 300 }}>
                      {l.provas.map((p, i) => <div key={i} style={{ fontSize: '0.74rem' }}>● <strong>{p.fonte}</strong> · {fmtData(p.data)}{p.detalhe ? <span style={{ color: P.muted }}> · {p.detalhe}</span> : ''}</div>)}
                    </td>
                    <td style={{ ...td, fontSize: '0.76rem' }}>
                      {l.aditivo
                        ? <><div style={{ fontWeight: 700, color: '#166534' }}>● {fmtData(l.aditivo.data)}</div><div style={{ color: P.muted }}>{l.aditivo.item}</div><div style={{ color: P.muted }}>interna {l.aditivo.interna}/3 · operadora {l.aditivo.externa}</div></>
                        : <div style={{ color: '#94a3b8' }}>○ não encontrado</div>}
                      <div style={{ fontSize: '0.7rem', color: l.foraDoPacote ? '#991b1b' : P.muted }} title={l.regra ? `${l.regra.contrato} — excluso: ${l.regra.excluso || '—'}` : ''}>{l.motivoPacote}</div>
                    </td>
                    <td style={{ ...td, maxWidth: 220 }}>
                      {l.pendencias.length === 0 ? <span style={chip('#dcfce7', '#166534')}>ok</span>
                        : l.pendencias.map(p => { const [f, c] = PEND_COR[p] || ['#dbeafe', '#1e40af']; return <div key={p} style={{ marginBottom: 2 }}><span style={chip(f, c)}>{p}</span></div>; })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${P.border}`, fontSize: '0.78rem', color: P.muted }}>{vis.length} troca(s)</div>
      </div>
    </div>
  );
}
